import { z } from 'zod';

import { advancedConfigurationSchema } from './advanced-config.schema';
import { toolOverridesSchema } from './agent-behavior-snapshot.schema';
import { agentGraphSchema } from './agent-graph.schema';
import { displayConfigSchema } from './custom-tool.schema';
import { toolSchema } from './tool.schema';

export type { Tool } from './tool.schema';

const timeStringSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:MM format');

export const providerSchema = z.enum(['gemini', 'openai']);

export type Provider = z.infer<typeof providerSchema>;

export const callProviderSchema = z.enum(['twilio', 'telnyx']);

export type CallProvider = z.infer<typeof callProviderSchema>;

const transferTargetCategoryKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(50)
  .regex(/^[a-z][a-z0-9_]*$/, 'Category key must be snake_case (e.g., finance_sales)');

export const transferTargetSchema = z.object({
  category_key: transferTargetCategoryKeySchema,
  category_description: z.string().trim().min(2).max(200),
  phone_number: z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{6,14}$/, 'Must be a valid E.164 phone number (e.g., +441234567890)'),
  sms_enabled: z.boolean().default(false),
  is_default: z.boolean().default(false),
  notify_emails: z.array(z.string().email()).default([]),
  active: z.boolean().default(true),
});

export type TransferTarget = z.infer<typeof transferTargetSchema>;

export const transferScheduleDaySchema = z.enum([
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]);

export type TransferScheduleDay = z.infer<typeof transferScheduleDaySchema>;

export const transferScheduleEntrySchema = z
  .object({
    day: transferScheduleDaySchema,
    enabled: z.boolean().default(false),
    start_time: timeStringSchema.default('08:00'),
    end_time: timeStringSchema.default('17:00'),
  })
  .refine(
    (entry) => {
      const [startHours, startMinutes] = entry.start_time.split(':').map(Number);
      const [endHours, endMinutes] = entry.end_time.split(':').map(Number);
      return startHours * 60 + startMinutes < endHours * 60 + endMinutes;
    },
    {
      message: 'End time must be after start time for each schedule day',
      path: ['end_time'],
    },
  );

export type TransferScheduleEntry = z.infer<typeof transferScheduleEntrySchema>;

export const transferScheduleSchema = z
  .array(transferScheduleEntrySchema)
  .max(7)
  .refine(
    (entries) => {
      const uniqueDays = new Set(entries.map((entry) => entry.day));
      return uniqueDays.size === entries.length;
    },
    {
      message: 'Transfer schedule cannot contain duplicate days',
    },
  )
  .default([]);

export type TransferSchedule = z.infer<typeof transferScheduleSchema>;

export const whatsappRoutingNumberSchema = z.object({
  number: z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{6,14}$/, 'Must be a valid E.164 phone number (e.g., +441234567890)'),
  // Shown in the picker when there's more than one number. Optional — a
  // single configured number never shows a label, it's just a direct link.
  label: z.string().trim().max(50).default(''),
});

export type WhatsappRoutingNumber = z.infer<typeof whatsappRoutingNumberSchema>;

export const widgetBrandingSchema = z.object({
  logo_url: z.string().url().optional(),
  logo_style: z.string().max(500).optional(),
  primary_color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Must be a hex color like #FF5500')
    .optional(),
  welcome_message: z.string().max(500).optional(),
  title: z.string().max(100).optional(),
  subtitle: z.string().max(100).optional(),
  tool_display_mode: z.enum(['inline', 'overlay']).optional(),
  // v2 = the floating "glass" widget; missing or v1 = the classic side panel.
  widget_version: z.enum(['v1', 'v2']).optional(),
  welcome_heading: z.string().max(100).optional(),
  // Tidied rather than rejected: a failed parse resets the whole branding object.
  suggested_questions: z
    .array(z.string())
    .transform((questions) =>
      questions
        .map((q) => q.trim().slice(0, 120))
        .filter(Boolean)
        .slice(0, 6),
    )
    .optional(),
});

export type WidgetBranding = z.infer<typeof widgetBrandingSchema>;

export const widgetPageTypeSchema = z.enum(['vehicle_detail', 'vehicle_listing', 'other']);

export type WidgetPageType = z.infer<typeof widgetPageTypeSchema>;

/**
 * Page awareness for the web widget: the widget reads the page the visitor is
 * on, the AI writes a welcome-bubble message about it, and the chat agent is
 * told which page the visitor is looking at. Off unless `enabled` is true.
 */
export const widgetPageContextSchema = z.object({
  enabled: z.boolean().default(false),
  page_types: z.array(widgetPageTypeSchema).default(['vehicle_detail']),
  delay_seconds: z.number().int().min(0).max(120).default(5),
  message_instructions: z.string().max(1000).default(''),
  // Widget voice calls open with an AI greeting about the page instead of the welcome message.
  call_greeting: z.boolean().default(true),
  // Path patterns where nothing happens, e.g. "/checkout/*". `*` matches anything.
  exclude_paths: z.array(z.string().trim().min(1).max(200)).max(50).default([]),
});

export type WidgetPageContext = z.infer<typeof widgetPageContextSchema>;

/** Reads stored page-awareness settings, falling back to defaults (feature off). */
export const normalizeWidgetPageContext = (value: unknown): WidgetPageContext => {
  const parsed = widgetPageContextSchema.safeParse(value ?? {});
  return parsed.success ? parsed.data : widgetPageContextSchema.parse({});
};

export const configurationVersionSchema = z.object({
  id: z.string().uuid(),
  configuration_id: z.string().uuid(),
  version_number: z.number().int(),
  prompt_template: z.string().min(10),
  text_prompt_template: z.string().optional().nullable(),
  welcome_message: z.string().optional().nullable(),
  missed_call_sms_message: z.string().optional().nullable(),
  followup_sms_message: z.string().optional().nullable(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
});

export type ConfigurationVersion = z.infer<typeof configurationVersionSchema>;

export const configurationSetupSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid().optional().nullable(),
  workflow_id: z.string().uuid().optional().nullable(),
  messaging_workflow_id: z.string().uuid().optional().nullable(),
  /** Runs once when a new session starts on any channel. */
  session_start_workflow_id: z.string().uuid().optional().nullable(),
  name: z.string().min(2),
  active_version_id: z.string().uuid().optional().nullable(),
  inbound_twilio_phone_number_id: z.string().uuid().optional().nullable(),
  default_outbound_twilio_phone_number_id: z.string().uuid().optional().nullable(),
  inbound_telnyx_phone_number_id: z.string().uuid().optional().nullable(),
  default_outbound_telnyx_phone_number_id: z.string().uuid().optional().nullable(),
  provider: providerSchema.default('gemini'),
  call_provider: callProviderSchema.default('twilio'),
  call_window_start: timeStringSchema,
  call_window_end: timeStringSchema,
  retry_limit: z.number().int().min(0).max(10),
  retry_delay_minutes: z.number().int().min(1).max(240),
  max_call_duration_minutes: z.number().int().min(1).max(20).default(10),
  voice_name: z.string().max(100).optional(),
  llm_model: z.string().optional(),
  text_llm_model: z.string().max(100).optional(),
  advanced: advancedConfigurationSchema.default({}),
  tools: z.array(toolSchema).default(['capture_availability', 'capture_consent']),
  custom_tool_ids: z.array(z.string().uuid()).default([]),
  // Per-config widget display for built-in tools, keyed by tool name
  // (e.g. `send_finance_link`). Mirrors custom_tools.display_config.
  builtin_tool_displays: z.record(z.string(), displayConfigSchema).default({}),
  // Per-config LLM-facing wording overrides for built-in and custom tools,
  // keyed by tool name. Applied where tool schemas are built for a session.
  tool_overrides: toolOverridesSchema.default({}),
  multi_agent_enabled: z.boolean().default(false),
  // When true the configuration is read-only: the backend rejects any edit
  // (update, version changes, delete) until it is unlocked. Only the flag
  // itself may be toggled while locked.
  locked: z.boolean().default(false),
  notify_on_exhausted_attempts: z.boolean().default(false),
  transfer_targets: z.array(transferTargetSchema).default([]),
  transfer_schedule: transferScheduleSchema,
  transfer_phone_number: z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/, 'Must be a valid E.164 phone number (e.g., +441234567890)')
    .optional()
    .nullable(),
  widget_enabled: z.boolean().default(false),
  widget_text_chat_enabled: z.boolean().default(true),
  widget_allowed_origins: z.array(z.string()).default([]),
  widget_rate_limit_per_hour: z.number().int().min(1).max(1000).default(10),
  widget_branding: widgetBrandingSchema.default({}),
  widget_custom_css: z.string().optional().nullable(),
  // When true, the embedded widget shows only the WhatsApp click-to-chat button
  // — no voice "Start Call" FAB and no chat panel. Requires whatsapp_number.
  widget_whatsapp_only: z.boolean().default(false),
  widget_page_context: widgetPageContextSchema.default(widgetPageContextSchema.parse({})),
  agent_graph: agentGraphSchema.optional().nullable(),
  text_agent_graph: agentGraphSchema.optional().nullable(),
  whatsapp_number: z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/, 'Must be a valid E.164 phone number (e.g., +441234567890)')
    .optional()
    .nullable(),
  // Additional WhatsApp routing numbers. When non-empty, the widget shows a
  // picker ("Chat to a Person" / "Chat to AI", etc.) instead of the single
  // whatsapp_number link above. Each entry needs a short label so visitors
  // know who they're choosing.
  whatsapp_numbers: z.array(whatsappRoutingNumberSchema).default([]),
  inbound_email_connector_id: z.string().uuid().optional().nullable(),
  inbound_email_address: z.string().email().optional().nullable(),
  email_template: z.string().optional().nullable(),
  // Per-configuration key the external Clients Dashboard sends as x-api-key.
  // Only used when the global DASHBOARD_API_KEY setting is empty.
  dashboard_api_key: z.string().max(255).optional().nullable(),
  prompt_rules: z.string().optional().nullable(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
  versions: z.array(configurationVersionSchema).optional(),
  active_version: configurationVersionSchema.optional().nullable(),
});

export type ConfigurationSetup = z.infer<typeof configurationSetupSchema>;

export const createConfigurationSetupSchema = configurationSetupSchema
  .omit({ id: true, created_at: true, updated_at: true, versions: true, active_version: true })
  .extend({
    name: z.string().min(2, 'Configuration name must be at least 2 characters'),
    prompt_template: z.string().min(10, 'Prompt template should provide guidance'),
    text_prompt_template: z.string().optional(),
    welcome_message: z.string().optional(),
    missed_call_sms_message: z.string().optional(),
    followup_sms_message: z.string().optional(),
  });

export type CreateConfigurationSetupDto = z.infer<typeof createConfigurationSetupSchema>;

/**
 * Zod 4 quirk that made PATCH updates data-destructive:
 * `.partial()` makes every key optional, but a `ZodDefault`-wrapped field
 * STILL emits its default value when the key is absent from the input. A
 * partial (PATCH) update therefore silently injected the schema default for
 * every omitted field — clobbering stored config values (e.g. wiping
 * `tools`, `custom_tool_ids`, `widget_allowed_origins` back to defaults when
 * only `advanced.language_code` was sent).
 *
 * To get a TRUE partial where omitted fields stay `undefined` (so the service
 * / Prisma skip them), we strip the `ZodDefault` wrappers from the create
 * shape BEFORE calling `.partial()`. The create schema keeps its defaults
 * untouched — only the update schema is defaults-free.
 */
type StripDefault<T> = T extends z.ZodDefault<infer Inner> ? Inner : T;
type StrippedShape<S extends z.ZodRawShape> = { [K in keyof S]: StripDefault<S[K]> };

function stripDefaultsFromShape<S extends z.ZodRawShape>(shape: S): StrippedShape<S> {
  return Object.fromEntries(
    Object.entries(shape).map(([key, fieldSchema]) => [
      key,
      fieldSchema instanceof z.ZodDefault ? fieldSchema.removeDefault() : fieldSchema,
    ]),
  ) as StrippedShape<S>;
}

export const updateConfigurationSetupSchema = z
  .object(stripDefaultsFromShape(createConfigurationSetupSchema.shape))
  .partial()
  .extend({
    editing_version_id: z.string().uuid().optional(),
  });

export type UpdateConfigurationSetupDto = z.infer<typeof updateConfigurationSetupSchema>;

export const createConfigurationVersionSchema = configurationVersionSchema.omit({
  id: true,
  configuration_id: true,
  version_number: true,
  created_at: true,
  updated_at: true,
});

export type CreateConfigurationVersionDto = z.infer<typeof createConfigurationVersionSchema>;
